import { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { helpdeskAiContentApi, isApiError, type HelpdeskAiContentKind } from '@shared/api';
import { aiContentDetailQuery, aiContentKeys } from '../api/queries';
import { outcomeUnknown } from './editor';

/** List and detail retain one deletion intent until its accepted operation is known. */
export function useAiContentDeletion({
  scope,
  kind,
  id,
  canDelete,
  onDeleted,
}: {
  scope: string;
  kind: HelpdeskAiContentKind;
  id: string;
  canDelete: boolean;
  onDeleted?: () => void;
}) {
  const client = useQueryClient();
  const { t } = useTranslation();
  const [confirm, setConfirm] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>();
  const [uncertain, setUncertain] = useState(false);
  const [acceptedId, setAcceptedId] = useState<string>();
  const submission = useRef(false);
  const intent = useRef<{ etag: string; key: string } | null>(null);
  const notified = useRef(false);
  const detail = useQuery(aiContentDetailQuery(scope, kind, id));
  // A different observed deletion cannot resolve a lost response to this intent.
  const operationId =
    acceptedId ?? (uncertain ? undefined : detail.data?.data.deletion?.operationId);
  const monitor = useQuery({
    queryKey: [...aiContentKeys.detail(scope, kind, id), 'operation', operationId],
    queryFn: ({ signal }) => {
      if (!operationId) throw new Error('Deletion operation is unavailable');
      return helpdeskAiContentApi.operation(operationId, signal);
    },
    enabled: Boolean(operationId),
    retry: false,
    refetchInterval: (query) =>
      query.state.error || (query.state.data && query.state.data.businessState !== 'accepted')
        ? false
        : 2000,
  });
  const blocker = useBlocker({
    shouldBlockFn: () =>
      !acceptedId && Boolean(intent.current) && (submission.current || uncertain),
    enableBeforeUnload: !acceptedId && Boolean(intent.current) && (pending || uncertain),
    withResolver: true,
  });
  const deleted = monitor.data?.businessState === 'succeeded';
  const deletionState =
    monitor.data?.businessState === 'failed'
      ? 'failed'
      : (detail.data?.data.deletion?.state ?? (acceptedId ? 'pending' : undefined));
  const denied =
    !canDelete ||
    [error, detail.error, monitor.error].some(
      (cause) => isApiError(cause) && [401, 403].includes(cause.status)
    );
  const refresh = () => {
    setError(undefined);
    void detail.refetch();
    if (operationId) void monitor.refetch();
  };
  const beginDelete = useCallback(async () => {
    if (submission.current || uncertain || !canDelete) return;
    if (operationId) {
      setConfirm(true);
      return;
    }
    submission.current = true;
    setPending(true);
    setConfirm(true);
    setError(undefined);
    try {
      const current = await helpdeskAiContentApi.detail(kind, id);
      client.setQueryData(aiContentKeys.detail(scope, kind, id), current);
      if (current.data.deletion) return;
      intent.current = { etag: current.etag, key: crypto.randomUUID() };
      setConfirm(true);
    } catch (cause) {
      setError(cause);
    } finally {
      submission.current = false;
      setPending(false);
    }
  }, [canDelete, client, id, kind, operationId, scope, uncertain]);
  const remove = async () => {
    if (submission.current || !canDelete || !intent.current || acceptedId) return;
    submission.current = true;
    setPending(true);
    setError(undefined);
    try {
      const result = await helpdeskAiContentApi.delete(
        kind,
        id,
        intent.current.etag,
        intent.current.key
      );
      setAcceptedId(result.data.operationId);
      setUncertain(false);
      setConfirm(true);
      void client.invalidateQueries({ queryKey: aiContentKeys.lists(scope, kind) });
      void detail.refetch();
    } catch (cause) {
      setError(cause);
      setConfirm(true);
      // A rejected recovery request does not prove the original request was rejected.
      const unknown = uncertain || outcomeUnknown(cause);
      setUncertain(unknown);
      if (!unknown) intent.current = null;
    } finally {
      submission.current = false;
      setPending(false);
    }
  };
  useEffect(() => {
    // The public resource may disappear after cleanup even when this actor cannot
    // read the original operation. Refresh the list without claiming its success.
    if (!uncertain && isApiError(detail.error) && detail.error.status === 404)
      void client.invalidateQueries({ queryKey: aiContentKeys.lists(scope, kind) });
  }, [client, detail.error, kind, scope, uncertain]);
  useEffect(() => {
    if (!deleted || notified.current) return;
    notified.current = true;
    void client.invalidateQueries({ queryKey: aiContentKeys.lists(scope, kind) });
    toast.success(t('aiContent.deletion.succeeded'));
    onDeleted?.();
  }, [client, deleted, kind, onDeleted, scope, t]);
  const closeConfirmation = (open: boolean) => {
    if (submission.current || uncertain) return;
    setConfirm(open);
    if (!open) intent.current = null;
  };
  return {
    detail,
    monitor,
    operationId,
    deleted,
    deletionState,
    unresolvedDelete: uncertain,
    pending,
    error,
    confirm,
    blocker,
    denied,
    refresh,
    beginDelete,
    remove,
    closeConfirmation,
  };
}
